/**
 * Bundle Variant Picker Handler
 * Updates product form variant ID, price display, and add-to-cart button state
 * when user selects Standard or + LED Light options
 */

(function () {
  'use strict';

  /**
   * Format price from cents to currency string
   */
  const formatPrice = (priceInCents) => {
    if (!priceInCents || priceInCents <= 0) return '';
    const price = priceInCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  /**
   * Find the product form and hidden variant ID input
   */
  const findFormElements = (sectionId) => {
    const productFormId = `product-form-${sectionId}`;

    // Try to find form by ID first
    let form = document.getElementById(productFormId);

    // If not found, try finding product-form custom element and then the form inside it
    if (!form) {
      const productFormElement = document.querySelector(`product-form#${productFormId}`);
      if (productFormElement) {
        form = productFormElement.querySelector('form');
      }
    }

    // Fallback: try to find any form with the product-form ID as a class or data attribute
    if (!form) {
      form = document.querySelector(`form#${productFormId}`);
    }

    // Final fallback: find product-form element and get its form
    if (!form) {
      const productFormEl = document.querySelector('product-form');
      if (productFormEl) {
        form = productFormEl.querySelector('form');
      }
    }

    if (!form) {
      console.warn('Form not found with ID:', productFormId);
      return null;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) {
      console.warn('Variant input not found in form');
      return null;
    }

    const submitButton = form.querySelector('[name="add"]');
    const submitButtonText = submitButton ? submitButton.querySelector('span') : null;

    return {
      form,
      variantInput,
      submitButton,
      submitButtonText
    };
  };

  /**
   * Find the price container element
   */
  const findPriceContainer = (sectionId) => {
    return document.getElementById(`price-${sectionId}`);
  };

  /**
   * Find the SKU container element
   */
  const findSkuContainer = (sectionId) => {
    return document.getElementById(`sku-${sectionId}`);
  };

  /**
   * Update the SKU display
   */
  const updateSkuDisplay = (skuContainer, sku) => {
    if (!skuContainer) return;

    const skuSpan = skuContainer.querySelector('span');
    if (skuSpan) {
      skuSpan.textContent = sku || '';
      console.log('Updated SKU span:', sku || '(empty)');
    } else {
      // If no span, update the container directly
      skuContainer.textContent = sku || '';
      console.log('Updated SKU container (direct):', sku || '(empty)');
    }
  };

  const updateSaleBadge = (priceContainer, price, compareAtPrice) => {
    if (!priceContainer) return;

    const saleBadge = priceContainer.querySelector('.price__badge-sale');
    if (!saleBadge) return;

    if (compareAtPrice > price && compareAtPrice > 0) {
      const savings = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
      saleBadge.textContent = `${savings}% OFF`;
      saleBadge.style.display = '';
      return;
    }

    saleBadge.textContent = '';
    saleBadge.style.display = 'none';
  };

  /**
   * Update the price display
   */
  const updatePriceDisplay = (priceContainer, variantData) => {
    console.log('Updating price display:', variantData);
    if (!priceContainer || !variantData) {
      console.warn('Cannot update price - missing container or data');
      return;
    }

    const price = variantData.price || 0;
    const compareAtPrice = variantData.compare_at_price || 0;
    const formattedPrice = formatPrice(price) + " USD";
    const formattedCompareAtPrice = formatPrice(compareAtPrice);

    console.log('Price update values:', {
      price,
      compareAtPrice,
      formattedPrice,
      formattedCompareAtPrice,
      isOnSale: compareAtPrice > price
    });

    if (!formattedPrice) {
      console.warn('No formatted price available');
      return;
    }

    // Find all price items
    const priceItems = priceContainer.querySelectorAll('.price-item');
    console.log('Found price items:', priceItems.length);

    if (!priceItems.length) {
      console.warn('No price items found in container');
      return;
    }

    const isOnSale = compareAtPrice > price && compareAtPrice > 0;

    // Toggle visibility of price containers and update parent price element classes
    const priceElement = priceContainer.querySelector('.price') || priceContainer.closest('.price');
    const priceRegularContainer = priceContainer.querySelector('.price__regular');
    const priceSaleContainer = priceContainer.querySelector('.price__sale');

    // Update parent price element classes to control visibility via CSS
    if (priceElement) {
      if (isOnSale) {
        priceElement.classList.add('price--on-sale');
        console.log('Added price--on-sale class');
      } else {
        priceElement.classList.remove('price--on-sale');
        console.log('Removed price--on-sale class');
      }
    }

    // Also ensure containers are visible/hidden
    if (priceRegularContainer && priceSaleContainer) {
      if (isOnSale) {
        // Show sale container, hide regular container
        priceRegularContainer.style.display = 'none';
        priceRegularContainer.classList.add('hidden');
        // priceRegularContainer.classList.add('visibility-hidden');
        priceSaleContainer.style.display = '';
        priceSaleContainer.classList.remove('hidden');
        priceSaleContainer.classList.remove('visibility-hidden');
        console.log('Showing sale container, hiding regular container');
      } else {
        // Show regular container, hide sale container
        priceRegularContainer.style.display = '';
        priceRegularContainer.classList.remove('hidden');
        priceRegularContainer.classList.remove('visibility-hidden');
        priceSaleContainer.style.display = 'none';
        priceSaleContainer.classList.add('hidden');
        // priceSaleContainer.classList.add('visibility-hidden');
        console.log('Showing regular container, hiding sale container');
      }
    }

    priceItems.forEach((priceItem, index) => {
      // Skip unit price items
      if (priceItem.closest('.unit-price')) {
        console.log(`Skipping unit price item ${index}`);
        return;
      }

      // Handle compare-at price (strikethrough)
      if (priceItem.tagName === 'S' || priceItem.closest('s')) {
        if (isOnSale && formattedCompareAtPrice) {
          priceItem.textContent = formattedCompareAtPrice;
          console.log(`Updated compare-at price item ${index}:`, formattedCompareAtPrice);
        }
        return;
      }

      // Handle sale price
      if (isOnSale) {
        if (priceItem.classList.contains('price-item--sale')) {
          priceItem.textContent = formattedPrice;
          console.log(`Updated sale price item ${index}:`, formattedPrice);
        }
      } else {
        // Regular price (not on sale)
        // Update regular price items that are NOT in strikethrough
        if (priceItem.classList.contains('price-item--regular') && !priceItem.closest('s')) {
          priceItem.textContent = formattedPrice;
          console.log(`Updated regular price item ${index}:`, formattedPrice);
        } else if (!priceItem.classList.contains('price-item--regular') && !priceItem.closest('s')) {
          // Fallback: update any price item that's not regular and not in strikethrough
          // This handles cases where the structure might be different
          priceItem.textContent = formattedPrice;
          console.log(`Updated fallback price item ${index}:`, formattedPrice);
        }
      }
    });

    // Also try to update price items that might be in price__regular or price__sale containers
    const priceRegular = priceContainer.querySelector('.price__regular .price-item--regular');
    const priceSale = priceContainer.querySelector('.price__sale .price-item--sale');

    if (isOnSale && priceSale) {
      priceSale.textContent = formattedPrice;
      console.log('Updated sale price from container:', formattedPrice);
    } else if (!isOnSale && priceRegular) {
      priceRegular.textContent = formattedPrice;
      console.log('Updated regular price from container:', formattedPrice);
    }

    // Fallback: Update any visible price item if nothing was updated
    // This ensures the price is always visible
    const allPriceItems = priceContainer.querySelectorAll('.price-item');
    let priceUpdated = false;
    allPriceItems.forEach((item) => {
      if (!item.closest('.unit-price') && !item.closest('s') && item.textContent.trim()) {
        priceUpdated = true;
      }
    });

    if (!priceUpdated && formattedPrice) {
      // If no price was updated, find the first visible price item and update it
      const firstVisiblePrice = priceContainer.querySelector('.price-item:not(.unit-price .price-item):not(s .price-item)');
      if (firstVisiblePrice) {
        firstVisiblePrice.textContent = formattedPrice;
        console.log('Updated first visible price item as fallback:', formattedPrice);
      }
    }

    updateSaleBadge(priceContainer, price, compareAtPrice);
  };

  /**
   * Update the add-to-cart button state
   */
  const updateAddToCartButton = (formElements, isAvailable) => {
    if (!formElements || !formElements.submitButton) return;

    const { submitButton, submitButtonText } = formElements;

    if (!isAvailable) {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-disabled', 'true');
      // if (submitButtonText && window.variantStrings) {
      //   submitButtonText.textContent = window.variantStrings.soldOut || 'Sold out';
      // }
    } else {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
      if (submitButtonText && window.variantStrings) {
        submitButtonText.textContent = window.variantStrings.addToCart || 'Add to cart';
      }
    }
  };

  /**
   * Determine if the selected size has a Lugano 12x24 LED option
   */
  const hasLugano12x24Option = (selectedSizeRadio) => {
    if (!selectedSizeRadio) return false;
    const isSize12x24 = selectedSizeRadio.getAttribute('data-is-size-12x24') === 'true';
    const luganoVariantId = selectedSizeRadio.getAttribute('data-lugano-variant-id');
    return isSize12x24 && !!luganoVariantId;
  };

  /**
   * Update product gallery image based on selection
   */
  const updateProductImage = (picker, sectionId, ledEnabled, useLuganoVariant = false) => {
    const selectedSizeRadio = picker.querySelector('input[name="bundle_size_option_' + sectionId + '"]:checked');
    if (!selectedSizeRadio) return;

    // Get image URL based on LED toggle state
    let imageUrl;
    if (useLuganoVariant) {
      imageUrl = selectedSizeRadio.getAttribute('data-lugano-image-url')
        || selectedSizeRadio.getAttribute('data-main-image-url');
    } else if (ledEnabled) {
      imageUrl = selectedSizeRadio.getAttribute('data-bundle-image-url');
    } else {
      imageUrl = selectedSizeRadio.getAttribute('data-main-image-url');
    }

    if (!imageUrl) return;

    console.log('Updating product image to:', imageUrl);

    // Find the media gallery and update the active image
    const mediaGallery = document.querySelector(`#MediaGallery-${sectionId}, media-gallery`);
    if (!mediaGallery) {
      console.warn('Media gallery not found for image update');
      return;
    }

    const activeImage = mediaGallery.querySelector('.is-active img, .product__media-item:first-child img');
    if (activeImage) {
      activeImage.src = imageUrl;
      activeImage.srcset = ''; // Clear srcset to prevent override
      console.log('Successfully updated active image src');
    } else {
      console.warn('Active image element not found in media gallery');
    }
  };

  /**
   * Update LED toggle availability based on selected size
   */
  const updateLedToggleAvailability = (picker, sectionId) => {
    const selectedSizeRadio = picker.querySelector('input[name="bundle_size_option_' + sectionId + '"]:checked');
    if (!selectedSizeRadio) return;

    const bundleVariantId = selectedSizeRadio.getAttribute('data-bundle-variant-id');
    const bundleAvailable = selectedSizeRadio.getAttribute('data-bundle-available') === 'true';
    const lugano12x24Enabled = hasLugano12x24Option(selectedSizeRadio);
    const luganoAvailable = selectedSizeRadio.getAttribute('data-lugano-available') === 'true';
    const ledYesRadio = picker.querySelector('input[id$="-led-yes"]');
    const ledNoRadio = picker.querySelector('input[id$="-led-no"]');
    const ledYesLabel = ledYesRadio ? picker.querySelector('label[for="' + ledYesRadio.id + '"]') : null;

    if (ledYesRadio && ledNoRadio) {
      if (lugano12x24Enabled) {
        ledYesRadio.disabled = !luganoAvailable;
        if (!luganoAvailable && ledYesRadio.checked) {
          ledNoRadio.checked = true;
        }
        return;
      }

      if (!bundleVariantId || !bundleAvailable) {
        // Bundle variant doesn't exist or is out of stock - disable Yes
        ledYesRadio.disabled = true;
        if (ledYesLabel) {
          // Add sold out text if not already present
          if (!ledYesLabel.querySelector('span')) {
            const soldOutSpan = document.createElement('span');
            soldOutSpan.style.color = 'rgba(var(--color-foreground), 0.5)';
            // soldOutSpan.textContent = ' - Sold Out';
            // ledYesLabel.appendChild(soldOutSpan);
          }
        }

        // If Yes is currently selected, switch to No
        if (ledYesRadio.checked) {
          ledNoRadio.checked = true;
          console.log('Switched to No because bundle variant is unavailable');
        }
      } else {
        // Bundle variant is available - enable Yes
        ledYesRadio.disabled = false;
        if (ledYesLabel) {
          // Remove sold out text if present
          const soldOutSpan = ledYesLabel.querySelector('span');
          if (soldOutSpan) {
            soldOutSpan.remove();
          }
        }
      }
    }
  };

  /**
   * Update variant based on selected size and LED toggle
   */
  const updateVariantFromToggle = (picker, sectionId) => {
    console.log('===== UPDATE VARIANT FROM TOGGLE =====');
    console.log('Picker:', picker);
    console.log('Section ID:', sectionId);

    if (!picker) {
      console.warn('No picker found');
      return;
    }

    // Get selected size radio
    const sizeRadioName = 'bundle_size_option_' + sectionId;
    const selectedSizeRadio = picker.querySelector('input[name="' + sizeRadioName + '"]:checked');
    console.log('Selected size radio:', selectedSizeRadio);
    if (!selectedSizeRadio) {
      console.warn('No size radio selected');
      return;
    }

    // Update LED toggle availability based on selected size
    updateLedToggleAvailability(picker, sectionId);

    const lugano12x24Enabled = hasLugano12x24Option(selectedSizeRadio);

    // Get selected LED toggle
    const ledRadioName = 'bundle_led_option_' + sectionId;
    const selectedLedRadio = picker.querySelector('input[name="' + ledRadioName + '"]:checked');
    console.log('Selected LED radio:', selectedLedRadio);
    if (!selectedLedRadio) {
      console.warn('No LED radio selected');
      return;
    }

    const ledEnabled = selectedLedRadio && selectedLedRadio.getAttribute('data-led-enabled') === 'true';
    const bundleVariantId = selectedSizeRadio.getAttribute('data-bundle-variant-id');
    const mainVariantId = selectedSizeRadio.getAttribute('data-main-variant-id');
    const luganoVariantId = selectedSizeRadio.getAttribute('data-lugano-variant-id');
    const useLuganoVariant = ledEnabled && lugano12x24Enabled && !!luganoVariantId;

    console.log('Toggle state:', {
      lugano12x24Enabled,
      useLuganoVariant,
      ledEnabled,
      luganoVariantId,
      bundleVariantId,
      mainVariantId,
      sizeValue: selectedSizeRadio.getAttribute('data-size-value'),
      bundleAvailable: selectedSizeRadio.getAttribute('data-bundle-available')
    });

    // Determine which variant to use
    let variantId, price, compareAtPrice, available, sku;

    if (useLuganoVariant) {
      variantId = luganoVariantId;
      price = parseInt(selectedSizeRadio.getAttribute('data-lugano-price') || '0', 10);
      compareAtPrice = parseInt(selectedSizeRadio.getAttribute('data-lugano-compare-at-price') || '0', 10);
      available = selectedSizeRadio.getAttribute('data-lugano-available') === 'true';
      sku = selectedSizeRadio.getAttribute('data-lugano-sku') || '';
      console.log('✅ Using Lugano 12x24 override variant:', {
        variantId,
        price,
        compareAtPrice,
        available,
        sku
      });
    } else if (ledEnabled && bundleVariantId) {
      // Use bundle variant
      variantId = bundleVariantId;
      price = parseInt(selectedSizeRadio.getAttribute('data-bundle-price'), 10);
      compareAtPrice = parseInt(selectedSizeRadio.getAttribute('data-bundle-compare-at-price') || '0', 10);
      available = selectedSizeRadio.getAttribute('data-bundle-available') === 'true';
      sku = selectedSizeRadio.getAttribute('data-bundle-sku') || '';
      console.log('✅ Using bundle variant (LED enabled):', {
        variantId,
        price,
        compareAtPrice,
        available,
        sku
      });
    } else {
      // Use main variant
      variantId = mainVariantId;
      price = parseInt(selectedSizeRadio.getAttribute('data-main-price'), 10);
      compareAtPrice = parseInt(selectedSizeRadio.getAttribute('data-main-compare-at-price') || '0', 10);
      available = selectedSizeRadio.getAttribute('data-main-available') === 'true';
      sku = selectedSizeRadio.getAttribute('data-main-sku') || '';
      console.log('✅ Using main variant (LED disabled):', {
        variantId,
        price,
        compareAtPrice,
        available,
        sku
      });
    }

    // Update product image
    updateProductImage(picker, sectionId, ledEnabled, useLuganoVariant);

    if (!variantId) {
      console.error('No variant ID found!');
      return;
    }

    // Store current state for persistence
    const currentState = {
      variantId,
      price,
      compareAtPrice,
      available,
      sku
    };

    // Find form elements
    const formElements = findFormElements(sectionId);
    if (!formElements) {
      console.error('Form elements not found for section:', sectionId);
      return;
    }

    console.log('Form elements found:', {
      form: formElements.form,
      variantInput: formElements.variantInput,
      currentValue: formElements.variantInput.value
    });

    // Update hidden variant ID input
    formElements.variantInput.value = variantId;
    console.log('Updated variant input value to:', variantId);
    console.log('Variant input value after update:', formElements.variantInput.value);

    // Dispatch change event for any listeners
    formElements.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    console.log('Dispatched change event on variant input');

    // Update price display - try to get price from JSON variant data if available
    const variantScript = picker.querySelector('script[type="application/json"]');
    let finalPrice = price;
    let finalCompareAtPrice = compareAtPrice;
    let finalSku = sku;

    if (variantScript) {
      try {
        const allVariants = JSON.parse(variantScript.textContent);
        const variant = allVariants.find(v => v.id === parseInt(variantId, 10));
        if (variant) {
          // Use JSON variant data as source of truth for price
          finalPrice = variant.price || price;
          finalCompareAtPrice = variant.compare_at_price || compareAtPrice;
          // Also update SKU from JSON if available
          if (variant.sku && variant.sku !== null && variant.sku !== '') {
            finalSku = variant.sku;
          }
          console.log('Using price from JSON variant:', {
            finalPrice,
            finalCompareAtPrice,
            finalSku,
            variantPrice: variant.price,
            variantCompareAtPrice: variant.compare_at_price,
            variantSku: variant.sku
          });
        } else {
          console.warn('Variant not found in JSON:', variantId);
        }
      } catch (error) {
        console.error('Error parsing variant JSON for price:', error);
      }
    }

    const priceContainer = findPriceContainer(sectionId);
    if (priceContainer) {
      console.log('Updating price container with:', {
        price: finalPrice,
        compare_at_price: finalCompareAtPrice,
        available
      });

      // Update immediately
      updatePriceDisplay(priceContainer, {
        price: finalPrice,
        compare_at_price: finalCompareAtPrice,
        available
      });

      // Update multiple times with increasing delays to override other scripts
      const updatePriceMultipleTimes = () => {
        const delays = [100, 250, 500, 750, 1000];
        delays.forEach((delay) => {
          setTimeout(() => {
            console.log(`Updating price after ${delay}ms delay`);
            updatePriceDisplay(priceContainer, {
              price: finalPrice,
              compare_at_price: finalCompareAtPrice,
              available
            });
          }, delay);
        });
      };

      updatePriceMultipleTimes();
    } else {
      console.warn('Price container not found for section:', sectionId);
    }

    // Update SKU display
    const skuContainer = findSkuContainer(sectionId);
    if (skuContainer) {
      console.log('Updating SKU container with:', finalSku || '(empty)');

      // Update immediately
      updateSkuDisplay(skuContainer, finalSku);

      // Update multiple times with delays to override other scripts
      const updateSkuMultipleTimes = () => {
        const delays = [100, 250, 500, 750, 1000];
        delays.forEach((delay) => {
          setTimeout(() => {
            console.log(`Updating SKU after ${delay}ms delay`);
            updateSkuDisplay(skuContainer, finalSku);
          }, delay);
        });
      };

      updateSkuMultipleTimes();
    } else {
      console.warn('SKU container not found for section:', sectionId);
    }

    // Update add-to-cart button
    updateAddToCartButton(formElements, available);

    // Update the variant-radios element's currentVariant property for sticky cart panel compatibility
    if (variantScript) {
      try {
        const allVariants = JSON.parse(variantScript.textContent);
        const variant = allVariants.find(v => v.id === parseInt(variantId, 10));
        if (variant) {
          // Set currentVariant on the variant-radios element so sticky cart panel can find it
          picker.currentVariant = {
            ...variant,
            available: available
          };
          console.log('Set currentVariant on variant-radios:', picker.currentVariant);
        } else {
          console.warn('Variant not found in JSON for currentVariant:', variantId);
        }
      } catch (error) {
        console.error('Error setting currentVariant:', error);
      }
    }

    // Store state on picker element for persistence
    picker.dataset.currentVariantId = variantId;
    picker.dataset.currentPrice = finalPrice;
    picker.dataset.currentSku = finalSku || '';

    console.log('===== END UPDATE VARIANT FROM TOGGLE =====');
  };

  /**
   * Initialize bundle variant picker
   */
  const initBundleVariantPicker = () => {
    console.log('SMARCH: Initializing bundle variant picker');
    const pickers = document.querySelectorAll('[data-bundle-variant-picker]');

    if (!pickers.length) return;

    pickers.forEach((picker) => {
      console.log('Processing picker:', picker);
      const sectionId = picker.getAttribute('data-section-id') || picker.getAttribute('data-section');
      if (!sectionId) {
        console.warn('Bundle variant picker: No section ID found');
        return;
      }
      console.log('Bundle variant picker: Found section ID:', sectionId);

      // Parse variants from JSON script
      const variantScript = picker.querySelector('script[type="application/json"]');
      if (variantScript) {
        try {
          console.log('SMARCH: Parsing variants from JSON script');
          const allVariants = JSON.parse(variantScript.textContent);

          // Separate main and bundle variants based on radio buttons
          const mainVariants = [];
          const bundleVariants = [];

          const radioButtons = picker.querySelectorAll('input[type="radio"][data-product-type]');
          radioButtons.forEach((radio) => {
            console.log('SMARCH: Processing radio button:', radio);
            const variantId = parseInt(radio.getAttribute('data-variant-id'), 10);
            const productType = radio.getAttribute('data-product-type');
            const variant = allVariants.find(v => v.id === variantId);

            if (variant) {
              if (productType === 'main') {
                mainVariants.push(variant);
              } else if (productType === 'bundle') {
                bundleVariants.push(variant);
              }
            }
          });

          // Remove duplicates (in case same variant appears multiple times)
          const uniqueMainVariants = Array.from(new Map(mainVariants.map(v => [v.id, v])).values());
          const uniqueBundleVariants = Array.from(new Map(bundleVariants.map(v => [v.id, v])).values());
        } catch (error) {
          console.error('Error parsing variant JSON:', error);
          console.error('Script content:', variantScript.textContent);
        }
      } else {
        console.warn('Bundle Variant Picker: No variant JSON script found');
      }

      // Initialize variant state based on default selections
      updateVariantFromToggle(picker, sectionId);

      // Store current variant state on picker element for persistence
      picker.dataset.bundlePickerInitialized = 'true';

      // Create a function to persist price and SKU based on current selections
      let isUpdating = false;
      const persistPriceAndSku = () => {
        if (isUpdating) return; // Prevent infinite loops
        isUpdating = true;

        const selectedSizeRadio = picker.querySelector('input[name="bundle_size_option_' + sectionId + '"]:checked');
        const selectedLedRadio = picker.querySelector('input[name="bundle_led_option_' + sectionId + '"]:checked');

        if (!selectedSizeRadio) {
          isUpdating = false;
          return;
        }

        if (!selectedLedRadio) {
          isUpdating = false;
          return;
        }

        const ledEnabled = selectedLedRadio && selectedLedRadio.getAttribute('data-led-enabled') === 'true';
        const lugano12x24Enabled = hasLugano12x24Option(selectedSizeRadio);
        const luganoVariantId = selectedSizeRadio.getAttribute('data-lugano-variant-id');
        const bundleVariantId = selectedSizeRadio.getAttribute('data-bundle-variant-id');
        const mainVariantId = selectedSizeRadio.getAttribute('data-main-variant-id');

        const variantId = ledEnabled && lugano12x24Enabled && luganoVariantId
          ? luganoVariantId
          : (ledEnabled && bundleVariantId ? bundleVariantId : mainVariantId);
        const expectedPrice = ledEnabled && lugano12x24Enabled && luganoVariantId
          ? parseInt(selectedSizeRadio.getAttribute('data-lugano-price') || '0', 10)
          : (ledEnabled && bundleVariantId
            ? parseInt(selectedSizeRadio.getAttribute('data-bundle-price') || '0', 10)
            : parseInt(selectedSizeRadio.getAttribute('data-main-price') || '0', 10));
        const expectedSku = ledEnabled && lugano12x24Enabled && luganoVariantId
          ? (selectedSizeRadio.getAttribute('data-lugano-sku') || '')
          : (ledEnabled && bundleVariantId
            ? (selectedSizeRadio.getAttribute('data-bundle-sku') || '')
            : (selectedSizeRadio.getAttribute('data-main-sku') || ''));

        if (!variantId) {
          isUpdating = false;
          return;
        }

        // Get price from JSON for accurate comparison
        const variantScript = picker.querySelector('script[type="application/json"]');
        let finalExpectedPrice = expectedPrice;
        let finalExpectedCompareAtPrice = 0;

        if (variantScript) {
          try {
            const allVariants = JSON.parse(variantScript.textContent);
            const variant = allVariants.find(v => v.id === parseInt(variantId, 10));
            if (variant) {
              finalExpectedPrice = variant.price || expectedPrice;
              finalExpectedCompareAtPrice = variant.compare_at_price || 0;
            }
          } catch (e) { }
        }

        // Check if price/SKU need to be corrected
        const priceContainer = findPriceContainer(sectionId);
        if (priceContainer) {
          const priceItems = priceContainer.querySelectorAll('.price-item');
          let needsUpdate = false;

          priceItems.forEach((item) => {
            if (item.closest('.unit-price') || item.closest('s')) return;
            const currentPriceText = item.textContent.trim();
            const expectedPriceText = formatPrice(finalExpectedPrice);
            if (currentPriceText && currentPriceText !== expectedPriceText && !currentPriceText.includes('From')) {
              needsUpdate = true;
            }
          });

          if (needsUpdate) {
            console.log('Price was overridden, re-applying correct price');
            updatePriceDisplay(priceContainer, {
              price: finalExpectedPrice,
              compare_at_price: finalExpectedCompareAtPrice,
              available: true
            });
          }
        }

        const skuContainer = findSkuContainer(sectionId);
        if (skuContainer && expectedSku) {
          const skuSpan = skuContainer.querySelector('span');
          const currentSku = skuSpan ? skuSpan.textContent.trim() : skuContainer.textContent.trim();
          if (currentSku !== expectedSku) {
            console.log('SKU was overridden, re-applying correct SKU');
            updateSkuDisplay(skuContainer, expectedSku);
          }
        }

        isUpdating = false;
      };

      // Watch for changes to price and SKU containers and re-apply if needed
      const priceContainer = findPriceContainer(sectionId);
      const skuContainer = findSkuContainer(sectionId);

      if (priceContainer || skuContainer) {
        const observer = new MutationObserver((mutations) => {
          // Only react if we're not the one making the change
          if (!isUpdating) {
            setTimeout(() => {
              persistPriceAndSku();
            }, 100);
          }
        });

        if (priceContainer) {
          observer.observe(priceContainer, { childList: true, subtree: true, characterData: true });
        }
        if (skuContainer) {
          observer.observe(skuContainer, { childList: true, subtree: true, characterData: true });
        }
      }

      // Attach event listeners for size and LED toggle changes
      picker.addEventListener('change', (event) => {
        const target = event.target;
        console.log('Change event detected on picker:', {
          target: target,
          tagName: target.tagName,
          type: target.type,
          classes: target.className,
          name: target.name
        });

        // Check if it's a size radio or LED toggle radio
        if (target.classList.contains('bundle-size-radio') || target.classList.contains('bundle-led-radio')) {
          console.log('✅ Size or LED toggle changed:', {
            isSize: target.classList.contains('bundle-size-radio'),
            isLed: target.classList.contains('bundle-led-radio'),
            value: target.value,
            name: target.name
          });

          // If size changed, update LED toggle availability first
          if (target.classList.contains('bundle-size-radio')) {
            updateLedToggleAvailability(picker, sectionId);
          }

          updateVariantFromToggle(picker, sectionId);
        } else {
          console.log('Change event ignored - not a bundle radio:', {
            hasSizeClass: target.classList.contains('bundle-size-radio'),
            hasLedClass: target.classList.contains('bundle-led-radio')
          });
        }
      });

      // Also attach direct listeners to each radio for debugging
      const sizeRadios = picker.querySelectorAll('.bundle-size-radio');
      const ledRadios = picker.querySelectorAll('.bundle-led-radio');

      console.log('Found radios:', {
        sizeRadios: sizeRadios.length,
        ledRadios: ledRadios.length
      });

      sizeRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          console.log('Size radio direct change:', radio.value);
          updateLedToggleAvailability(picker, sectionId);
          updateVariantFromToggle(picker, sectionId);
        });
      });

      ledRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          console.log('LED radio direct change:', radio.value, 'LED enabled:', radio.getAttribute('data-led-enabled'));
          updateVariantFromToggle(picker, sectionId);
        });
      });

      // Initialize LED toggle availability on load
      updateLedToggleAvailability(picker, sectionId);
    });
  };

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleVariantPicker);
  } else {
    initBundleVariantPicker();
  }
})();
